import React from "reactn";
import { _, Backbone } from "js/vendor";
import { app } from "js/namespaces";
import Adapter from "js/core/storage/adapter";
import Thumbnails from "js/core/models/thumbnails";
import { UndoType } from "common/constants";
import { isOfflinePlayer, isRenderer } from "js/config";
import { ds } from "./dataService";
import StorageModel from "../storage/storageModel";
import { downloadFromUrl, trackActivity } from "../utilities/utilities";
import log from "../logger";
import SlidesAdapter from "../storage/slidesAdapter";
import Profiler from "../profiler";
import { setGlobal } from "reactn";
import { THUMBNAIL_SIZES, AssetType, TaskState } from "common/constants";
import { FeatureType } from "./features";
import DummyAdapter from "../storage/dummyAdapter";
import { loadImage } from "js/core/utilities/promiseHelper";
import Api from "js/core/api";
import getSlideText from "common/utils/getSlideText";
import { ShowDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import NotificationsService from "js/core/services/notifications";
import pusher from "js/core/services/pusher";

const profiler = Profiler.create({
    name: "Slide",
    type: Profiler.AVG_LOAD_TIME,
    warnTime: 400
});

const nonTextKeys = {
    content_type: true,
    content_value: true
};

const SOURCE = {
    local: 0,
    remote: 1,
    server: 2,
    initialize: 3
};

const Slide = StorageModel.extend({

    root: "slides",
    profiler: profiler,
    readonly: isOfflinePlayer,
    hasChanges: false,
    // Has to be set to true in order to make sure
    // the initial model is always normalized by
    // the adapter before firing the load event
    ensurePersisted: true,

    createAdapter: function(options) {
        return new SlidesAdapter({ ...options, model: this });
    },

    getDummyAdapterOptions() {
        return {
            removeTrailingNullsFromArrays: true
        };
    },

    initialize: function(model, options) {
        this.type = "Slide";
        this.destroyed = false;
        this.dataStateIndex = 0;
        this.once("load", () => {
            this.dataStateIndex = 0;
            this._updateDataState({ source: SOURCE.initialize });
        });
        this.presentation = options.presentation;
    },

    isMigrated: function() {
        if (this.adapter instanceof SlidesAdapter) {
            return !this.adapter.fallbackToCommandsAdapter;
        }

        return false;
    },

    hasNextState: function() {
        const states = this.get("states");
        return states && this.dataStateIndex + 1 < states.length;
    },

    nextState: function() {
        if (!this.hasNextState()) {
            return;
        }
        this.dataStateIndex++;
        this._updateDataState({ source: SOURCE.local });
    },

    generateLoadPromise() {
        return StorageModel.prototype.generateLoadPromise.call(this).then(() => {
            const loaders = [];
            if (ds.assets) {
                loaders.push(ds.assets.loadAssetsForSlide(this));
            }
            return Promise.all(loaders);
        }).then(() => {
            const templateId = this.get("template_id");
            const version = this.get("version");

            // Hack to mitigate the bug that we had in ppt importer when it wasn't setting
            // version on the generated slide models
            if (templateId === "ppt-slide" && !version) {
                this.update({ version: 9 }, { save: false });
            }

            return this;
        });
    },

    setDataState: function(index) {
        if (!this.loaded) {
            return;
        }
        this.dataStateIndex = index;
        this._updateDataState({ source: SOURCE.local });
    },

    debug: function() {
        console.log("-----------------------------------------------");
        console.log("Slide:" + this.id + " index: " + ds.selection.presentation.getSlideIndex(this.id));
        console.log("Template: " + this.get("template_id"));
        console.log(JSON.stringify(this.get("states")[0], null, 2));
    },

    handleRemoteChange: function(type, data) {
        const changeSet = StorageModel.prototype.handleRemoteChange.call(this, type, data);

        // The initialize type is handled when the slide loads, so skip double firing here.
        if (type === Adapter.TYPE.initialize) {
            return;
        }

        if (changeSet.hasUpdates) {
            this._updateDataState({ source: this._isDataStateChangeSet(changeSet) ? SOURCE.remote : SOURCE.server });
        }
    },

    update: function(attrs, options = {}) {
        if (window.isPlayer) {
            log.warn("Trying to commit when data should be read only");
            options = Object.assign({}, options, { save: false });
        }

        const changeSet = StorageModel.prototype.update.call(this, attrs, options);
        if (!options.isCommit && changeSet.hasUpdates && this._isDataStateChangeSet(changeSet)) {
            this._updateDataState(Object.assign(options, { source: SOURCE.local }));
        }

        if (changeSet.hasUpdates && !options.isFromMigration) {
            const updatedKeys = Object.keys(changeSet.update);
            // We don't want to mark the slide as changed if it was only the modifiedAt
            // or assignedUser value that got updated (which we do when we want to generate new
            // thumbnails, i.e. when the theme of the presentation gets updated)
            if (updatedKeys.length !== 1 || (updatedKeys[0] !== "modifiedAt" && updatedKeys[0] !== "assignedUser")) {
                this.hasChanges = true;
            }
        }

        return changeSet;
    },

    getLibraryItem() {
        const orgId = this.presentation.get("orgId");
        const libraryItemId = this.get("libraryItemId");
        if (!orgId || !libraryItemId) {
            return null;
        }
        return ds.teams.defaultTeamForOrg(orgId).libraryItems.get(libraryItemId);
    },

    // detaches the current adapter so that any changes to the slide model will not save automatically
    // call saveChanges() to restore original adapter and commit any changes since detach was called
    detachAdapter: function() {
        this.isDetached = true;

        // store the original adapter and current state
        this.originalAdapter = this.adapter;
        this.originalState = _.cloneDeep(this.attributes);

        this.adapter = new DummyAdapter(this.getDummyAdapterOptions());
    },

    // reattaches detached adapter
    reattachAdapter: function() {
        if (this.isDetached) {
            this.isDetached = false;
            this.adapter = this.originalAdapter;
        }
    },

    // reattaches the detached adapter and saves any changes since detached
    saveChanges: async function() {
        if (this.isDetached) {
            this.isDetached = false;

            const newState = _.cloneDeep(this.attributes);

            // silently restore the model to the original state from when we detached the adapter
            this.update(this.originalState, {
                removeMissingKeys: true,
                silent: true,
                save: false
            });

            // restore the original adapter and update to the new state
            this.adapter = this.originalAdapter;
            const { hasUpdates } = this.update(newState, {
                removeMissingKeys: true,
                silent: true,
                save: true
            });

            await this.updatePromise;

            return { hasUpdates };
        } else {
            throw new Error("saveChanges can only be called after detachAdapter() has been called on the model");
        }
    },

    _isDataStateChangeSet: function(changeSet) {
        return "template_id" in changeSet.original || "layout" in changeSet.original || "states" in changeSet.original;
    },

    _reloadDataState: function(options) {
        // Make sure elements is defined even if there are no
        // states because the canvas will be mutating it and will
        // crash if it is not an object
        let elements = {};
        const states = this.get("states");
        if (states && states[this.dataStateIndex]) {
            elements = _.cloneDeep(states[this.dataStateIndex]);
        }

        // Layout and animations have to be mutation-safe and always defined as well
        const layout = _.cloneDeep(this.get("layout") ?? {});
        const animations = _.cloneDeep(this.get("animations") ?? {});

        // This will contain a set of data which will be manipulated
        // by the canvas, so we have to make sure that mutating it is safe
        // hence clone deep elements and layout
        this.dataState = {
            elements,
            layout,
            animations,
            template_id: this.get("template_id"),
            version: this.get("version")
        };

        if (!options.silent) {
            this.trigger("updateDataState", this, Object.assign(options, {
                remoteChange: options.source === "remote",
                initialize: options.source === "initialize"
            }));
        }
    },

    _updateDataState: function(options) {
        switch (options.source) {
            case SOURCE.local:
                log.info(log.SLIDE)(`[${this.id}] local change triggered`);
                this._reloadDataState(options);
                break;
            case SOURCE.remote:
                log.info(log.SLIDE)(`[${this.id}] remote change triggered`);
                this._reloadDataState(options);
                break;
            case SOURCE.initialize:
                log.info(log.SLIDE)(`[${this.id}] init change triggered`);
                this._reloadDataState(options);
                break;
            case SOURCE.server:
                log.info(log.SLIDE)(`[${this.id}] server change triggered`);
                break;
            default:
                log.warn("Source unknown: " + options.source);
                break;
        }
    },

    /**
     * This will push the dataState to the model.
     * @param silent If true, no events will be emitted. this can prevent feedback loops in cases where we
     * don't want the display to update.
     */
    commit: function(options = {}) {
        if (isRenderer || window.isPlayer) {
            log.warn("Trying to commit when data should be read only");
            options = Object.assign({}, options, { save: false });
        }

        // dataState contains element models as well as other props that are part of
        // the slide model so we merge them into the slide model but we'll remove elements
        // from the root and add them to the states prop
        //
        // NOTE: we have to clone deep this.attributes to make sure we don't mutate them
        // when mutating the data object
        let data = { ..._.cloneDeep(this.attributes), ...this.dataState };
        delete data.elements;

        // States may have been removed if there were no elements on the slide
        if (!data.states) {
            data.states = [this.dataState.elements];
            // Force data state index to zero since we have only one state
            this.dataStateIndex = 0;
        } else {
            // Assigning elements to the current data state index
            data.states[this.dataStateIndex] = this.dataState.elements;
        }

        // This clone deep is mandatory because the update function in some cases
        // can mount subobjects of the data objects to the model's attributes
        // in a non-mutation safe manner so we can end up with data state having
        // references to the model's attributes
        data = _.cloneDeep(data);

        // Special case to avoid unnecessary slide model updates
        const annotations = data.states[this.dataStateIndex].annotations;
        const prevAnnotations = this.attributes.states ? this.attributes.states[this.dataStateIndex]?.annotations : null;
        if (_.isEqual(annotations, { items: [], connections: { items: [] } }) && !prevAnnotations) {
            data.states[this.dataStateIndex].annotations = null;
        }

        // Preserve the original state in case we need to save an undo step
        const saveUndo = app.undoManager && options.undo !== false;
        const forceUndo = saveUndo && options.forceUndo;
        const originalAttrs = saveUndo ? _.cloneDeep(this.attributes) : null;

        // Update the model
        const { hasUpdates } = this.update(
            data,
            {
                removeMissingKeys: true,
                isCommit: true,
                isFromMigration: _.defaultTo(options.isFromMigration, false)
            }
        );

        if ((hasUpdates || forceUndo) && saveUndo) {
            const { undoOldState = {}, undoNewState = {} } = options;

            app.undoManager.set(UndoType.SLIDE_DATA, this.id, {
                attributes: originalAttrs,
                ...undoOldState
            }, {
                attributes: this.attributes,
                ...undoNewState
            });
        }

        return this.dataState;
    },

    discardChanges: function() {
        this._updateDataState({ source: SOURCE.local, silent: true });
    },

    disconnect: function() {
        this.off();

        // Make sure we finished editing before disconnecting
        this.finishedEditing()
            .then(() => this.adapter.disconnect());
    },

    destroy: function() {
        throw new Error("Not supported");
    },

    finishedEditing: async function() {
        if (this.hasChanges && !this.isDetached) {
            this.hasChanges = false;

            const timestamp = new Date().getTime();

            try {
                StorageModel.prototype.update.call(this, { modifiedAt: timestamp });
                await this.updatePromise;

                const libraryItem = this.getLibraryItem();
                if (libraryItem) {
                    // When team slide is updated, update libraryItem metadata to reflect when and who updated
                    libraryItem.update({ contentModifiedAt: timestamp });
                    await libraryItem.updatePromise;
                }

                if (this.presentation) {
                    // If the slide is the first slide of its presentation
                    if (this.presentation.getSlideIndex(this.id) === 0) {
                        this.presentation.update({ firstSlideModifiedAt: timestamp });
                        await this.presentation.updatePromise;
                    }

                    if (!this.presentation.disconnected) {
                        // Fire and forget
                        NotificationsService.notifyOnSlideEdited(this.presentation.id, this.id)
                            .catch(err => console.error(err));
                    }

                    if (!this.presentation.disconnected) {
                        // Fire and forget
                        Api.slideText.post({ slideId: this.id })
                            .catch(err => console.error(err));
                    }
                }
            } catch (err) {
                log.error(new Error(`Slide modifiedAt set failed ${err.message}`));

                ShowWarningDialog({
                    title: "Something went wrong.",
                    message: <>
                        <span>
                            There may be a temporary problem with your internet connection,<br />
                            or the permissions for this presentation may have changed.<br />
                            If the problem persists, please contact <a href="mailto:support@beautiful.ai">support@beautiful.ai</a>
                        </span>
                    </>,
                    acceptCallback: async () => {
                        if (this.presentation.invalid) {
                            //to ensure the presentation library shows the correct permission for the presentation refetch the user permissions
                            await this.presentation.getUserPermissions(true);
                        }
                        app.router.navigate("/", { trigger: true });
                    },
                });
            } finally {
                trackActivity("Slide", "ContentModified", null, null, {
                    "slide_id": this.id,
                    "slides_created": 0,
                }, { skipAmplitude: true });
            }
        }
    },

    getSlideText() {
        return getSlideText(this.dataState.elements);
    },

    hasMultipleStages() {
        return this.get("template_id") === "textgrid_carousel";
    },

    getPlaybackStageCount() {
        if (this.hasMultipleStages()) {
            return this.dataState.elements.primary.items.length;
        }
        return 1;
    },

    async getThumbnailUrl(presentationId = null, size = "small", stageIndex = 0, preventModelLoad = false, fallbackTimestamp = null) {
        log.info(log.SLIDE)(`[${this.id}] getThumbnailUrl() presentationId: ${presentationId} size: ${size} stageIndex: ${stageIndex} preventModelLoad: ${preventModelLoad} fallbackTimestamp: ${fallbackTimestamp}`);

        if (!THUMBNAIL_SIZES[size]) {
            throw new Error(`Unknown thumbnail size ${size}`);
        }

        if (window.isOfflinePlayer) {
            const url = `/userData/thumbnails/${this.id}${stageIndex === 0 ? "" : `-${stageIndex}`}`;
            await loadImage(url);
            return url;
        }

        if (!this.loaded && !preventModelLoad) {
            await this.load();
        }

        let timestamp;
        if (this.loaded) {
            timestamp = this.get("modifiedAt");
        } else if (fallbackTimestamp) {
            timestamp = fallbackTimestamp;
        } else {
            timestamp = this.instantiatedAt;
        }

        return Thumbnails.getSignedUrlAndLoad(this.id, timestamp, presentationId, THUMBNAIL_SIZES[size].suffix, stageIndex);
    },

    async generateJpegs() {
        if (!this.disconnected) {
            await this.load();
        }

        const theme = app.themeManager.loadThemeFromPresentation(this.presentation);
        if (!theme.disconnected) {
            await theme.load();
        }

        const { channelId } = await Api.jpegs.post({
            slideModel: this.attributes,
            theme: theme.attributes,
            presentationId: this.presentation.id,
            hideBaiBranding: app.user.features.isFeatureEnabled(FeatureType.REMOVE_BAI_BRANDING, this.presentation.getWorkspaceId()),
            hideSmartSlideWatermark: app.user.features.isFeatureEnabled(FeatureType.SMART_SLIDES, this.presentation.getWorkspaceId()),
            isTeamUser: app.user.features.isFeatureEnabled(FeatureType.TEAMS, this.presentation.getWorkspaceId()),
        });

        const { urls } = await pusher.waitForEvent(channelId, TaskState.FINISHED, TaskState.ERROR, 2 * 60 * 1000);
        return urls;
    },

    exportSlideToJpeg() {
        trackActivity("Presentation", "JPEGExport", null, null, {}, { audit: true });
        setGlobal({
            isLoadingDialogOpen: true
        });

        const dialog = ShowDialog(ProgressDialog, {
            title: "Exporting Slide to JPEG...",
        });

        const exportSlide = async () => {
            const urls = await this.generateJpegs();

            for (const [idx, url] of urls.entries()) {
                await downloadFromUrl(url, `Slide ${this.getIndex() + 1}${idx !== 0 ? `-${idx}` : ""}.jpeg`);
            }

            setGlobal({
                isLoadingDialogOpen: false
            });
            dialog.props.closeDialog();
        };

        exportSlide()
            .catch(err => {
                setGlobal({
                    isLoadingDialogOpen: false
                });
                dialog.props.closeDialog();
                log.error(err);
            });
    },

    getIndex() {
        return ds.selection.presentation.getSlideIndex(this.id);
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        // Loading thumbnails of all sizes
        await Promise.all(Object.keys(THUMBNAIL_SIZES).map(size => this.getThumbnailUrl(this.presentation.id, size)));
        return attrs;
    },

    toText: function() {
        const text = [];

        function toText(obj) {
            if (typeof obj === "string" && obj !== "") {
                text.push(obj);
            } else if (_.isObject(obj)) {
                Object.keys(obj).forEach(key => {
                    if (!nonTextKeys[key]) {
                        toText(obj[key]);
                    }
                });
            }
        }

        toText(this.get("states"));
        text.push(this.get("slide_notes"));
        return text.join(" ");
    },

    isLibrarySlide: function() {
        return this.has("libraryItemId");
    },

    isLibrarySlideInCurrentUserOrg: function() {
        const orgId = this.presentation.get("orgId");
        if (this.presentation.getWorkspaceId() !== "personal") {
            const defaultTeam = ds.teams.defaultTeamForOrg(orgId);
            if (defaultTeam.has("sharedResources") && defaultTeam.get("sharedResources")[1]) {
                if (defaultTeam.get("sharedResources")[1][this.get("libraryItemId")]) {
                    return true;
                }
            }
        }
        return false;
    },

    hasAudibleVideoAsset: function() {
        let hasAudibleVideo = false;
        const checkForVideo = object => {
            if (!object) {
                return;
            }

            if (!_.isObject(object)) {
                return;
            }

            if (
                object.videoAssetId ||
                object.videoId ||
                (
                    (object.content_type === AssetType.STOCK_VIDEO || object.content_type === AssetType.VIDEO) &&
                    object.content_value
                )
            ) {
                if (!object.assetProps?.muted) {
                    hasAudibleVideo = true;
                    return;
                }
            }

            Object.keys(object).forEach(key => {
                return checkForVideo(object[key]);
            });
        };
        checkForVideo(this.get("states"));

        return hasAudibleVideo;
    }

});

const Slides = Backbone.Collection.extend({
    model: Slide,

    url: function() {
        return "slides";
    },

    initialize: function() {
        this.type = "Slides";
    }
});

export { Slide, Slides };
